<template>
  <tr
      :class="[
          item.submitted ? 'submitted' : '',
          'active',
          ]"
      @click="setWorkerIfNotAdmin"
  >
    <td v-if="worker.payment_reference_type === 'W'" class="name_col" @click="setWorkerIfNotAdmin">
      {{ worker.first_name }} {{ worker.surname }} - {{ worker.job_title }}
    </td>
    <td v-else class="name_col" @click="setWorkerIfNotAdmin">
      {{worker.company_trading_name}} - {{ worker.first_name }} {{ worker.surname }}
    </td>

    <td class="rate_col">
      <router-link :to="{name: 'worker_profile', query:{worker_id: item.worker_id}}">
        ({{item.payment_reference_type}}) {{ item.payment_reference }}
      </router-link>
    </td>

    <td class="rate_col">
      <Rate :data="item.rate"/>
    </td>

    <template v-if="item && item.payments && item.geo && !isGeoAllZero(item.geo)">
      <td class="geo_col">
        <button
            :disabled="item.submitted || adminActive"
            class="geo_button"
            type="button"
            @click="applyGeoToPayments(item)"
        >
          Geo
        </button>
      </td>
    </template>
    <td v-else class="geo_col">-</td>

    <td :class="'status_col ' + item.status_name.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '')">{{item.status_name}}</td>

    <template v-if="item && item.payments">
      <td
          v-for="(tsPayment, payIndex) in item.payments"
          :key="`pay-${item.id}-${payIndex}`"
          class="day_col"
      >
        <validation-provider
            v-slot="{ errors }"
            :name="`${item.payments[payIndex].pay_date}`"
            rules="payday_required|absolute_numeric_strict"
        >
          <payday_input_horizontal
              v-model="item.payments[payIndex]"
              :disabled="item.submitted"
              :errors="errors"
              :rowIndex="payIndex"
              :timesheetId="item.id"
              @validate="() => validateRow(payIndex)"
              @advanceFocus="handleAdvanceFocus"
          />
        </validation-provider>
      </td>
    </template>
    <template v-else>
      <td
          v-for="(payItem, payIndex) in item.payments"
          :key="`pay-ro-${item.id}-${payIndex}`"
          class="day_col"
      >
        <payday_input_horizontal
            v-model="item.payments[payIndex]"
            :disabled="true"
            :rowIndex="payIndex"
            :timesheetId="item.id"
            @click="setWorkerIfNotAdmin"
            @validate="() => validateRow(payIndex)"
            @advanceFocus="handleAdvanceFocus"
        />
      </td>
    </template>

    <td class="checkbox_col">
      <template v-if="item && item.payments">
        <checkbox_input
            ref="backCheckbox"
            v-model="item.back_next_week"
            :disabled="item.submitted"
            :hideTextValue="true"
            class="switch"
            name="back_next_week"
            @keydown.native="handleRowGenericKeydown"
        />
      </template>
      <checkbox_input
          v-else
          v-model="item.back_next_week"
          :disabled="true"
          :hideTextValue="true"
          class="switch"
          name=""
      />
    </td>

    <td class="checkbox_col">
      <template v-if="item && item.payments">
        <checkbox_input
            ref="retentionCheckbox"
            v-model="item.retention_released"
            :disabled="item.submitted"
            :hideTextValue="true"
            class="switch"
            name="retention_released"
            value="true"
            @keydown.native="handleRowGenericKeydown"
        />
      </template>
      <checkbox_input
          v-else
          v-model="item.retention_released"
          :disabled="true"
          :hideTextValue="true"
          class="switch"
          name=""
      />
    </td>

    <template v-if="item && item.payments">
      <td class="total_col">
        {{ totalPriceForHours | currency }}
      </td>
      <td class="total_col">
        {{ totalAdditionsDeductions | currency }}
      </td>
      <td class="total_col">
        {{ (totalPriceForHours + totalAdditionsDeductions) | currency }}
      </td>
    </template>

    <td class="button_col">
      <template v-if="adminActive">
        <template v-if="item.status_name === 'paid'">
          <AdminReopenTimesheetButton
              :company-id="company_id"
              :timesheet-id="item.id"
              :admin-busy="adminBusy"
              :with-admin-lock="withAdminLock"
              :success="async ()=>{await onRefresh(item.id);}"
              :hard="true"
          />
          <AdminResetTimesheetButton
              :company-id="company_id"
              :timesheet-id="item.id"
              :admin-busy="adminBusy"
              :with-admin-lock="withAdminLock"
              :success="async ()=>{await onRefresh(item.id);}"
              :hard="true"
          />
        </template>
        <template v-else>
          <AdminReopenTimesheetButton
              :company-id="company_id"
              :timesheet-id="item.id"
              :admin-busy="adminBusy"
              :with-admin-lock="withAdminLock"
              :success="async ()=>{await onRefresh(item.id);}"
          />
          <AdminResetTimesheetButton
              :company-id="company_id"
              :timesheet-id="item.id"
              :admin-busy="adminBusy"
              :with-admin-lock="withAdminLock"
              :success="async ()=>{await onRefresh(item.id);}"
          />
        </template>
      </template>
      <template v-else>
        <template v-if="item && item.payments">
          <template v-if="!item.submitted">
            <button
                ref="submitButton"
                :disabled="saving || loading"
                class="timesheet_button"
                name="save_for_later"
                type="submit"
                @click="handleSave"
                @keydown="handleSaveKeydown"
            >
              <span v-if="!saving">Save</span>
              <span v-else><i class="fas fa-spinner fa-spin"></i></span>
            </button>

            <button
                ref="editButton"
                :disabled="saving || loading"
                class="timesheet_button"
                @keydown="handleEditKeydown"
                @click="onEditClick($event,item)"
            >
              <span v-if="!saving">Edit</span>
              <span v-else><i class="fas fa-spinner fa-spin"></i></span>
            </button>

            <button
                ref="finaliseButton"
                :disabled="saving || loading"
                class="timesheet_button"
                name="finalise"
                type="submit"
                @click="handleFinalise"
                @keydown="handleRowGenericKeydown"
            >
              <span v-if="!saving">Finalise</span>
              <span v-else><i class="fas fa-spinner fa-spin"></i></span>
            </button>
          </template>

          <div v-else class="row_status">
            <router-link
                :to="{ name: 'a_timesheet_edit_worker', params: { company_id, start_date, end_date, order_job_id: item.order_job_id, timesheet_id: item.id } }"
            >
              <div>Submitted</div>
              <button
                  ref="editButton"
                  :disabled="saving || loading"
                  class="timesheet_button"
                  @keydown="handleEditKeydown"
              >
                View
              </button>
            </router-link>
          </div>
        </template>
      </template>
    </td>
  </tr>
</template>

<style scoped>
@import '../../components/shared/css/workers-table.css';
</style>

<script>
import { isGeoAllZero } from '../../components/shared';
import checkbox_input from '../../components/timesheet_edit_timesheet/toggle_checkbox.vue';
import PaydayInputHorizontal from '../../components/timesheet_edit_timesheet/payday_input_horizontal.vue';
import Rate from '../../components/shared/Rate.vue';
import AdminReopenTimesheetButton from './AdminReopenTimesheetButton.vue';
import AdminResetTimesheetButton from './AdminResetTimesheetButton.vue';

export default {
  components: {
    checkbox_input,
    payday_input_horizontal: PaydayInputHorizontal,
    Rate,
    AdminResetTimesheetButton,
    AdminReopenTimesheetButton
  },
  props: {
    company_id: {required: true},
    loading: { type: Boolean, required: true },
    saving: { type: Boolean, required: true },
    handleSave: { type: Function, required: true },
    handleFinalise: { type: Function, required: true },
    applyGeoToPayments: { type: Function, required: true },
    item: { type: Object, required: true },
    worker: { type: Object, required: true },
    setWorker: { type: Function, required: true },
    totalPriceForHours: { required: true },
    totalAdditionsDeductions: { required: true },
    start_date: { required: true },
    end_date: { required: true },
    index: { type: Number, required: true },
    adminActive: { type: Boolean, required: true },
    adminBusy: { type: Boolean, required: true },
    withAdminLock: { type: Function, required: true },
    onRefresh: { type: Function, required: true },
  },
  data() {
    return {
      lastNavTime: null,
      lastDirection: null,
      singleNavTimer: null
    };
  },
  methods: {
    setWorkerIfNotAdmin() {
      if (this.adminActive) return;
      this.setWorker(this.index);
    },
    async onEditClick(event, item) {
      if (this.saving || this.loading) return;

      // prevent any parent handlers / form submits
      event.preventDefault();
      event.stopPropagation();

      try {
        // pass event through if your save logic needs it
        const ok = await this.handleSave?.(event);
        if (ok === false) return;

        const to = {
          name: 'a_timesheet_edit_worker',
          params: {
            company_id: this.company_id,
            start_date: this.start_date,
            end_date: this.end_date,
            order_job_id: item.order_job_id,
            timesheet_id: item.id
          }
        };

        // support cmd/ctrl/middle-click style behavior
        if (event.metaKey || event.ctrlKey || event.button === 1) {
          const { href } = this.$router.resolve(to);
          window.open(href, '_blank');
        } else {
          await this.$router.push(to);
        }

        this.$refs.editButton?.blur?.();
      } catch (err) {
        console.error('Edit navigation aborted:', err);
        this.$snotify?.error('Unable to save before navigating.', { timeout: 2000 });
      }
    },
    isGeoAllZero,

    // ----- focus helpers -----
    findFirstFocusable(root) {
      if (!root) return null;
      return root.querySelector(
          'input, button, [tabindex]:not([tabindex="-1"])'
      );
    },
    getRowFocusables() {
      const row = this.$el;
      const inputs = Array.from(row.querySelectorAll('.day_col input:not([disabled])'));

      const back = this.findFirstFocusable(this.$refs.backCheckbox && this.$refs.backCheckbox.$el);
      const retention = this.findFirstFocusable(this.$refs.retentionCheckbox && this.$refs.retentionCheckbox.$el);

      const save = this.$refs.submitButton || null;
      const edit = this.$refs.editButton || null;
      const finalise = this.$refs.finaliseButton || null;

      return [
        ...inputs,
        back,
        retention,
        save,
        edit,
        finalise
      ].filter(Boolean);
    },
    navigateFromActive(direction) {
      const items = this.getRowFocusables();
      if (!items.length) return;

      const active = document.activeElement;
      let idx = items.indexOf(active);
      if (idx === -1) {
        // try to infer nearest input
        const closestInput = this.$el.querySelector('.day_col input:focus');
        idx = closestInput ? items.indexOf(closestInput) : 0;
      }

      let nextIdx = idx + direction;
      if (nextIdx < 0) nextIdx = 0;
      if (nextIdx >= items.length) nextIdx = items.length - 1;

      const target = items[nextIdx];
      if (target) {
        target.focus();
        if (target.tagName === 'INPUT' && target.type !== 'checkbox') {
          try { target.select(); } catch (_) {}
        }
      }
    },

    focusSaveButton() {
      const btn = this.$refs.submitButton;
      if (btn && typeof btn.focus === 'function') btn.focus();
    },
    focusEditButton() {
      const btn = this.$refs.editButton;
      if (btn && typeof btn.focus === 'function') btn.focus();
    },
    focusFinaliseButton() {
      this.$refs.finaliseButton?.focus();
    },
    focusLastDayInput() {
      const allInputs = [...this.$el.querySelectorAll('.day_col input')];
      const lastInput = allInputs[allInputs.length - 1];
      lastInput?.focus();
      lastInput?.select?.();
    },

    // ----- child edge notifications -----
    handleAdvanceFocus({ edge, direction }) {
      // Move to the next/prev element in our row order
      this.navigateFromActive(direction);
    },

    // ----- checkboxes & finalise generic nav -----
    handleRowGenericKeydown(e) {
      let direction = null;
      switch (e.key) {
        case 'Tab': direction = e.shiftKey ? -1 : 1; break;
        case 'ArrowRight': direction = 1; break;
        case 'ArrowLeft': direction = -1; break;
        default: return; // let Enter/Space toggle checkbox or activate button
      }
      e.preventDefault();
      this.navigateFromActive(direction);
    },

    // ----- Save button special behavior (kept), but backward now navigates properly -----
    handleSaveKeydown(e) {
      let direction = null;
      switch (e.key) {
        case 'Tab': direction = e.shiftKey ? -1 : 1; break;
        case 'Enter':
        case 'ArrowRight': direction = 1; break;
        case 'ArrowLeft': direction = -1; break;
        default: return;
      }

      // Backward goes to previous element in row order (e.g., retention checkbox)
      if (direction === -1) {
        e.preventDefault();
        this.navigateFromActive(-1);
        return;
      }

      // Forward - preserve single vs double press behavior
      e.preventDefault();
      const now = Date.now();
      const DELAY = 400;

      if (this.lastNavTime && this.lastDirection === 1 && now - this.lastNavTime < DELAY) {
        clearTimeout(this.singleNavTimer);
        this.lastNavTime = null;
        this.lastDirection = null;
        this.$emit('save', e);
        // Double press - move to Edit
        this.focusEditButton();
      } else {
        clearTimeout(this.singleNavTimer);
        this.lastNavTime = now;
        this.lastDirection = 1;
        // Single press - emit save-tab after a short delay
        this.singleNavTimer = setTimeout(() => {
          this.$emit('save-tab', e);
          this.lastNavTime = null;
          this.lastDirection = null;
        }, DELAY);
      }
    },

    // Edit button - forward loops to Save; backward moves to previous (e.g., retention)
    handleEditKeydown(e) {
      let direction = null;
      switch (e.key) {
        case 'Tab': direction = e.shiftKey ? -1 : 1; break;
        case 'Enter':
        case 'ArrowRight': direction = 1; break;
        case 'ArrowLeft': direction = -1; break;
        default: return;
      }
      e.preventDefault();
      if (direction === 1) {
        this.focusSaveButton();
      } else {
        this.navigateFromActive(-1);
      }
    }
  }
};
</script>
